import { useEffect, useMemo, useRef, useState } from "react";
import QrPlacer from "./QrPlacer";
import {
    CANVAS, INK_FREE_ZONE, QR_DEFAULT, qrCanvasOrigin, SAFE_MARGIN, TRIM, withSampleData,
    type QrPlacement,
    type MailPieceSize,
} from "../mailPieceTemplates";

/*
    Seeing the card before spending a week finding out (MPN-1774).

    The HTML box gave no idea what the card looked like. The test send does -- it renders a
    real PDF through Lob -- but it costs a round trip and needs an address, which is too much
    friction for "is the headline too long". This draws the artwork as you type.

    WHAT IT IS HONEST ABOUT. This is your browser rendering the HTML, not Lob's. It is the
    right shape, the right size and the right margins, and it is not a proof: the printer runs
    an older engine and does not support flexbox or `inset`. The test send remains the thing
    that tells the truth, and the button for it sits right here.

    The overlays are the point as much as the artwork. Three of the four things that ruin a
    postcard are invisible in the HTML -- the trim, the address block and the QR code -- so
    they are drawn on top: what gets cut off, what Lob prints over, and where the code lands.
*/

interface MailPiecePreviewProps {
    size: MailPieceSize;
    html: string;
    side: "front" | "back";
    /** Shown in the printed address block, so the card reads the way it will arrive. */
    returnAddress?: { name?: string } | null;
    /**
     * Whether to draw the print guides.
     *
     * On by default, because what gets cut off and what the printer stamps over are the two
     * things you cannot see in the artwork itself. Off when somebody wants to look at the
     * card as it will arrive, without three rectangles drawn across it.
     */
    showGuides?: boolean;
    /** Where the QR code sits, in finished-card inches. Defaults to the platform placement. */
    qrPlacement?: QrPlacement;
    /** Supplied only by the editor. Without it the code is drawn where it prints and is not
        draggable, which is what every read-only surface wants. */
    onQrPlacementChange?: (next: QrPlacement) => void;
    /**
     * Whether to fill merge fields with a sample name.
     *
     * On for the editor, where the question is how the card reads. Off for approval, where
     * `{{first_name}}` left as written is the only way the person signing off can notice a
     * field nobody wired up -- a sample name makes an unfilled field look finished.
     */
    substituteSampleData?: boolean;
    /**
     * Accessible name for the artwork frame. Defaults to "front preview" / "back preview";
     * surfaces showing several cards at once should name the piece instead.
     */
    title?: string;
}

/*
    The reserved block's outline.

    Not red any more. Red reads as "this is wrong", and the first question it produced was
    why the address block crosses the safe-area guide. It is supposed to: Lob's reserved area
    is measured on the full-bleed canvas and reaches 0.15in from the finished right edge,
    while the safe margin for your own copy sits further in at 0.375in. The block is Lob's,
    not yours, so it is drawn as something reserved rather than something broken.
*/
const GUIDE = "#64748b";

/**
 * A QR-shaped pattern, not a real code.
 *
 * The printed one encodes this card's own link, which does not exist until the piece is
 * created, so drawing a scannable code here would be a lie about which link it carries. What
 * matters at this stage is the size and the contrast: a designer needs to see how much of the
 * card it takes and that nothing dark sits under it. Deterministic so it does not shimmer as
 * the artwork is edited.
 */
const QR_GRID = 21;
const QR_CELLS: boolean[] = (() => {
    const cells: boolean[] = [];
    for (let y = 0; y < QR_GRID; y++) {
        for (let x = 0; x < QR_GRID; x++) {
            const finder =
                (x < 7 && y < 7) || (x >= QR_GRID - 7 && y < 7) || (x < 7 && y >= QR_GRID - 7);
            if (finder) {
                // The three corner squares a scanner looks for: a ring with a solid core.
                const fx = x < 7 ? x : x - (QR_GRID - 7);
                const fy = y < 7 ? y : y - (QR_GRID - 7);
                const ring = fx === 0 || fx === 6 || fy === 0 || fy === 6;
                const core = fx >= 2 && fx <= 4 && fy >= 2 && fy <= 4;
                cells.push(ring || core);
            } else {
                // A fixed hash rather than Math.random, so the pattern is stable between
                // renders and between people looking at the same card.
                cells.push(((x * 7 + y * 13 + x * y * 3) % 5) < 2);
            }
        }
    }
    return cells;
})();

/** Bar heights for the Intelligent Mail barcode strip: tall, short, tall... */
const BARCODE: boolean[] = Array.from({ length: 48 }, (_, i) => (i * 5 + (i % 3)) % 3 !== 0);

/** Where Lob's reserved block starts, measured from the left of the canvas. */
function reservedLeft(size: MailPieceSize): number {
    const zone = INK_FREE_ZONE[size];
    return CANVAS[size].width - zone.right - zone.width;
}

/** Where it starts, measured from the top. */
function reservedTop(size: MailPieceSize): number {
    const zone = INK_FREE_ZONE[size];
    return CANVAS[size].height - zone.bottom - zone.height;
}

/**
 * The outline of the area your own copy can safely occupy, in canvas inches.
 *
 * The front is a plain rectangle inside the safe margin. The back is the same rectangle with
 * Lob's reserved corner bitten out of it, because that corner is not yours: Lob stamps the
 * address, barcode and indicia over whatever is underneath.
 */
function safeOutline(size: MailPieceSize, side: "front" | "back"): string {
    const canvas = CANVAS[size];
    const x0 = SAFE_MARGIN;
    const y0 = SAFE_MARGIN;
    const x1 = canvas.width - SAFE_MARGIN;
    const y1 = canvas.height - SAFE_MARGIN;

    if (side === "front") {
        return `${x0},${y0} ${x1},${y0} ${x1},${y1} ${x0},${y1}`;
    }

    // The same two numbers the block is drawn from, so the guide turns exactly where the
    // block begins and the two share their right and bottom edges.
    const left = reservedLeft(size);
    const top = reservedTop(size);

    return [
        `${x0},${y0}`,
        `${x1},${y0}`,
        `${x1},${top}`,
        `${left},${top}`,
        `${left},${y1}`,
        `${x0},${y1}`,
    ].join(" ");
}

/** The mock code. Not a real QR -- Lob generates the scannable one per recipient -- so this
    only has to read as a code at a glance and sit exactly where the real one will. */
function QrGlyph() {
    return (
        <div style={{
            width: "100%", height: "100%", display: "grid",
            gridTemplateColumns: `repeat(${QR_GRID}, 1fr)`,
            gridTemplateRows: `repeat(${QR_GRID}, 1fr)`,
        }}>
            {QR_CELLS.map((on, i) => (
                <span key={i} style={{ background: on ? "#111" : "transparent" }} />
            ))}
        </div>
    );
}

export default function MailPiecePreview(
    {
        size, html, side, returnAddress, showGuides = true,
        qrPlacement, onQrPlacementChange,
        substituteSampleData = true, title,
    }: MailPiecePreviewProps,
) {
    const canvas = CANVAS[size];

    /*
        Measured rather than given. This sits in a panel whose width depends on the editor
        around it, and a card scaled to a guessed width is either clipped or surrounded by
        dead space -- on a 6x11, badly.
    */
    const hostRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;
        const measure = () => setWidth(host.clientWidth);
        measure();
        // Not available in jsdom, so the tests fall back to the one-off measure above.
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(host);
        return () => observer.disconnect();
    }, []);
    // 96 CSS px to the inch, which is what the iframe will lay the inch units out at.
    const naturalWidth = canvas.width * 96;
    const scale = width / naturalWidth;
    // The cut, in on-screen pixels, so the shading matches the real margin at any size.
    const trimPx = Math.max(1, Math.round((TRIM / canvas.width) * (width || naturalWidth)));

    // The stored placement, or the platform default for every card authored before it was
    // editable. Null here means "wherever the platform puts it", not "no code".
    const placement = qrPlacement ?? QR_DEFAULT;
    const qrOrigin = qrCanvasOrigin(placement);

    const pct = useMemo(() => ({
        x: (inches: number) => `${(inches / canvas.width) * 100}%`,
        y: (inches: number) => `${(inches / canvas.height) * 100}%`,
    }), [canvas.width, canvas.height]);

    const box = {
        position: "absolute" as const,
        boxSizing: "border-box" as const,
        pointerEvents: "none" as const,
    };

    return (
        // Height comes from the card's own proportions rather than from the measured width,
        // so the panel is the right shape on the very first paint and the guides have
        // something to sit on before the measurement lands.
        <div
            ref={hostRef}
            style={{
                position: "relative", width: "100%",
                aspectRatio: `${canvas.width} / ${canvas.height}`,
                background: "#f8fafc", overflow: "hidden",
            }}
        >
            {html.trim() ? (
                <iframe
                    // No scripts: this is artwork, and the HTML can be pasted from anywhere.
                    sandbox=""
                    srcDoc={substituteSampleData ? withSampleData(html) : html}
                    title={title ?? `${side} preview`}
                    scrolling="no"
                    style={{
                        width: naturalWidth, height: canvas.height * 96, border: "none",
                        transform: `scale(${scale})`, transformOrigin: "top left",
                        position: "absolute", top: 0, left: 0,
                    }}
                />
            ) : (
                <div style={{
                    position: "absolute", top: "42%", width: "100%", textAlign: "center",
                    fontSize: 11, color: "#94a3b8",
                }}>
                    Nothing on the {side} yet
                </div>
            )}

            {/*
                Two guides, not four boxes.

                This used to draw a dashed rectangle for the trim and a dotted one for the
                safe area, and on the back two more for the address block and the QR. Four
                overlapping outlines on a small preview read as part of the design rather
                than as annotations, and none of them said which was which.

                The trim is now shown as what it is -- the strip that gets cut away -- by
                shading it rather than outlining it. Losing a border makes the card's real
                edge the strongest line on screen, which is the one that matters.
            */}
            {showGuides && (
                <>
                    {/*
                        The strip that gets cut away, shaded rather than outlined so the
                        card's real edge stays the strongest line on screen.
                    */}
                    <div style={{
                        ...box, top: 0, left: 0, right: 0, bottom: 0,
                        boxShadow: `inset 0 0 0 ${trimPx}px rgba(15,23,42,0.16)`,
                    }} />

                    {/*
                        The finished edge, as an actual line.

                        Shading alone was not enough to judge against: at 10% opacity there
                        was no visible edge, so the address block -- which sits 0.15in inside
                        it, by Lob's spec -- looked like it ran off the card. Somebody asked
                        twice whether it was aligned, which is the sign that the drawing was
                        not answering the question. A crisp line answers it: everything that
                        prints sits inside this, and the shaded strip outside it is lost to
                        the cut.
                    */}
                    <div style={{
                        ...box,
                        top: trimPx, left: trimPx, right: trimPx, bottom: trimPx,
                        border: "1px solid rgba(15,23,42,0.45)",
                    }} />

                    {/*
                        Where your copy is safe.

                        On the back this is an L, not a rectangle, and that shape is the whole
                        point. A rectangle claims the entire card is yours, so Lob's address
                        block -- which is anchored to the bottom-right corner and reaches
                        closer to the edge than your copy may -- cuts straight through it. It
                        looked like the block was overflowing. Nothing was overflowing; the
                        guide was drawing a boundary that does not exist.

                        So the guide now stops where Lob's area begins. The two shapes sit
                        beside each other, and the space left is exactly the space a headline
                        can use.
                    */}
                    <svg
                        style={{ ...box, top: 0, left: 0, width: "100%", height: "100%" }}
                        viewBox={`0 0 ${canvas.width} ${canvas.height}`}
                        preserveAspectRatio="none"
                    >
                        <polygon
                            points={safeOutline(size, side)}
                            fill="none"
                            stroke="rgba(100,116,139,0.6)"
                            /*
                                Inches, like everything else in the viewBox: the container
                                keeps the card's aspect ratio, so this scales evenly and stays
                                a hairline at any preview size. Deliberately NOT
                                `vectorEffect="non-scaling-stroke"` -- that reinterprets these
                                as screen pixels, and 0.012px draws nothing at all.
                            */
                            strokeWidth={0.012}
                            strokeDasharray="0.05 0.04"
                        />
                    </svg>
                </>
            )}

            {side === "back" && (
                <>
                    {/* What Lob actually stamps here, drawn as it prints rather than as an
                        empty rectangle. A labelled box told you the area was reserved; it did
                        not show that a headline placed nearby collides with a real address
                        block, which is the thing worth seeing before paying to print. */}
                    <div style={{
                        /*
                            Drawn out to the safe margin, not to Lob's exact outer edge.

                            Lob's block reaches 0.275in from the right of the canvas and the
                            safe guide sits at 0.375in, so drawing it at full extent left it
                            poking 0.1in past the guide -- two boundaries a hair apart that
                            never line up, which reads as the box overflowing no matter how
                            the borders are styled.

                            The left and top edges are Lob's real ones, and those are the ones
                            that matter: they decide whether a headline collides with the
                            address. The sliver trimmed off the right and bottom is already
                            outside the safe margin, so nothing that should be drawn there
                            stops being warned about.
                        */
                        ...box,
                        right: pct.x(SAFE_MARGIN), bottom: pct.y(SAFE_MARGIN),
                        width: pct.x(canvas.width - SAFE_MARGIN - reservedLeft(size)),
                        height: pct.y(canvas.height - SAFE_MARGIN - reservedTop(size)),
                        background: "#ffffff",
                        /*
                            A dashed guide, not a solid box.

                            Lob's area ends 0.15in from the finished right edge and 0.125in
                            from the bottom -- measured, not assumed. At preview size that is
                            about seven pixels, so a hard border next to the card-edge line
                            put two near-parallel solid lines a few pixels apart, and the eye
                            reads that as a box bursting out of a box every single time. In
                            the same dashed language as the safe guide it reads as what it is:
                            an annotation describing reserved space.
                        */
                        border: showGuides
                            ? "1px dashed rgba(100,116,139,0.55)"
                            : "1px solid #e2e8f0",
                        // Visible, so the label can sit just above the block. The printed
                        // content inside is still clipped by its own wrapper.
                        overflow: "visible",
                    }}
                    title={
                        "Lob prints the address, barcode and postage here after rendering your "
                        + "artwork. It reaches closer to the edge than the safe margin for your "
                        + "own copy, which is Lob's specification rather than a mistake."
                    }>
                        {/* Says whose area this is. Without it the block reads as artwork that
                            has overflowed, because it legitimately crosses the safe guide. */}
                        {showGuides && (
                            <span style={{
                                position: "absolute", right: 0, bottom: "100%",
                                marginBottom: 2, fontSize: Math.max(7, width * 0.0115),
                                color: GUIDE, letterSpacing: "0.04em",
                                textTransform: "uppercase", whiteSpace: "nowrap",
                                fontFamily: "Helvetica, Arial, sans-serif",
                            }}>
                                Lob prints here
                            </span>
                        )}
                        <div style={{
                            position: "absolute", inset: 0, padding: "6% 5%",
                            overflow: "hidden",
                            fontFamily: "Helvetica, Arial, sans-serif", color: "#111",
                            fontSize: Math.max(4, width * 0.018), lineHeight: 1.25,
                        }}>
                            {/* The return address, from the campaign's own settings when it
                                has them. */}
                            <div style={{ opacity: 0.75, fontSize: "0.82em" }}>
                                {(returnAddress?.name || "Your return address").toUpperCase()}
                            </div>

                            {/* The postage mark sits top-right of the block. */}
                            <div style={{
                                position: "absolute", right: "5%", top: "6%",
                                border: "1px solid #444", padding: "3% 5%",
                                fontSize: "0.72em", textAlign: "center", lineHeight: 1.1,
                            }}>
                                POSTAGE<br />INDICIA
                            </div>

                            {/* The Intelligent Mail barcode. Bars rather than a grey strip,
                                because its height is what a designer needs to keep clear. */}
                            <div style={{
                                position: "absolute", left: "5%", right: "5%", top: "46%",
                                height: "12%", display: "block", whiteSpace: "nowrap",
                                overflow: "hidden",
                            }}>
                                {BARCODE.map((tall, i) => (
                                    <span key={i} style={{
                                        display: "inline-block", width: "1.1%",
                                        marginRight: "0.5%", background: "#111",
                                        height: tall ? "100%" : "55%",
                                        verticalAlign: tall ? "top" : "middle",
                                    }} />
                                ))}
                            </div>

                            <div style={{ position: "absolute", left: "5%", top: "64%" }}>
                                <div style={{ fontWeight: 600 }}>JORDAN REYES</div>
                                <div>123 CAMPUS DRIVE</div>
                                <div>MAYS LANDING, NJ 08330</div>
                            </div>
                        </div>
                    </div>

                    {/* The QR, drawn as a code rather than a grey square. Representative
                        only -- the real one encodes this card's own link -- but it shows the
                        size and contrast a scanner needs, which a label cannot. */}
                    {/*
                        Just the code. The light plate behind it comes from the artwork
                        itself -- every back template and every uploaded image draws one --
                        so painting another here stacked two boxes on top of each other and
                        made the code look like a stamp on a sticker.

                        Drawn from the stored placement rather than a constant, and draggable
                        when the editor passes a change handler. Read-only surfaces get the
                        same square in the same spot without the handles.
                    */}
                    {onQrPlacementChange ? (
                        <QrPlacer
                            size={size}
                            placement={placement}
                            onChange={onQrPlacementChange}
                            canvas={canvas}
                        >
                            <QrGlyph />
                        </QrPlacer>
                    ) : (
                        <div style={{
                            ...box,
                            left: pct.x(qrOrigin.left), bottom: pct.y(qrOrigin.bottom),
                            width: pct.x(qrOrigin.size), height: pct.y(qrOrigin.size),
                        }} title="Every card gets its own code">
                            <QrGlyph />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
